/**
 * The plan.
 *
 * ── What this is, and where it comes from ────────────────────────────────────
 *
 * Ported from travel-day, which exists because the useful answer to "my flight
 * is at 3" is not the flight time — it is *when to walk out the front door*.
 * The same is true of a ferry, and more so: the Ceiba terminal is forty minutes
 * from San Juan, the car lane closes an hour before the boat, and parking is a
 * real queue rather than a formality. Somebody who reads "departs 15:30" and
 * leaves at 14:45 has already missed it.
 *
 * Everything is computed BACKWARDS from departure. The sailing is the fixed
 * point; each step before it claims the time it needs, and `leaveHomeAt` falls
 * out of the chain rather than being guessed at the end.
 *
 * Pure, and in `shared/` on purpose: the server needs the same cutoffs to refuse
 * a late booking, and two copies of these numbers that quietly disagree is a
 * booking the API accepts and the plan says is impossible.
 */

/**
 * The tunable middle of the app.
 *
 * Every number is a Ceiba–Vieques figure, deliberately cautious, because being
 * early costs a coffee and being late costs the day — the next boat may be
 * hours away and on a bad day there is not one.
 */
export const RULES = {
  /**
   * Foot-passenger check-in closes this long before departure.
   *
   * Also the booking cutoff: there is no point selling a ticket to somebody who
   * cannot be checked in with it. Operations run about thirty minutes; this
   * matches rather than second-guesses them.
   */
  passengerCutoffMinutes: 30,
  /**
   * Vehicle check-in closes this long before departure.
   *
   * Twice the passenger figure and it is not padding. A car has to be in the
   * lane, measured, and driven aboard in an order the crew decides — the deck is
   * loaded by weight and by who is getting off first. Arriving with a car at the
   * passenger cutoff means watching it sail without you.
   */
  vehicleCutoffMinutes: 60,
  /** Boarding opens this long before departure. */
  boardingOpensMinutes: 25,
  /** Be in the terminal this long before check-in closes, not at the cutoff. */
  terminalBufferMinutes: 15,
  /**
   * Parking at the terminal, from arriving to being on foot with your bag.
   *
   * Ceiba's lot fills on a Friday and the overflow is a walk. Zero when somebody
   * is being dropped off, which is why this is keyed on how they arrive.
   */
  parkingMinutes: { drive: 20, dropoff: 3, taxi: 3, walk: 0 },
  /** Buying a ticket at the counter, if they have not already. */
  counterMinutes: 15,
  /** Getting from the car park or the kerb to the terminal door. */
  toTerminalMinutes: { drive: 8, dropoff: 2, taxi: 2, walk: 2 },
} as const

export type ArriveBy = keyof typeof RULES.parkingMinutes

export type StepKind =
  | 'leave' | 'travel' | 'park' | 'arrive' | 'counter'
  | 'vehicleCutoff' | 'checkIn' | 'boarding' | 'depart' | 'arriveDest'

export interface PlanStep {
  id: string
  kind: StepKind
  title: string
  detail?: string
  /** UTC instant, in milliseconds. */
  at: number
  /** How long the activity itself takes, when that is meaningful. */
  durationMinutes?: number
  /** Miss this one and you miss the boat. */
  isDeadline?: boolean
}

export interface PlanWarning {
  level: 'info' | 'warn' | 'error'
  message: string
}

export interface FerryPlan {
  steps: PlanStep[]
  warnings: PlanWarning[]
  /** When they have to walk out the front door. The number people actually want. */
  leaveHomeAt: number | null
  /** The last moment a ticket is any use — vehicle cutoff if bringing a car. */
  cutoffAt: number
  departAt: number
  arriveAt: number
  doorToDoorMinutes: number | null
}

export interface PlanInput {
  scheduledDeparture: number
  scheduledArrival: number
  /** Actual departure when the sailing has been delayed; the plan follows it. */
  actualDeparture?: number | null
  route: string
  vesselName: string
  /** Minutes from where they are to the terminal. Null when unknown. */
  travelMinutes?: number | null
  arriveBy?: ArriveBy
  bringingVehicle?: boolean
  /** Already paid — no counter step. */
  hasTicket?: boolean
  /** Per-sailing overrides; null falls back to RULES. */
  passengerCutoffMinutes?: number | null
  vehicleCutoffMinutes?: number | null
  status?: string
  /** Not a passenger run: 'hazmat' (footnote), 'cargo' (the PDF's service icon),
   *  or 'unconfirmed' (abierto has no service type for it). */
  restricted?: string | null
}

const MIN = 60_000
const place = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s)

/**
 * The cutoff for a given booking.
 *
 * Exported because the SERVER uses it to refuse a late booking. One definition,
 * so the API and the plan cannot disagree about whether you can still travel.
 */
export function cutoffMinutes(input: {
  bringingVehicle?: boolean
  passengerCutoffMinutes?: number | null
  vehicleCutoffMinutes?: number | null
}): number {
  return input.bringingVehicle
    ? input.vehicleCutoffMinutes ?? RULES.vehicleCutoffMinutes
    : input.passengerCutoffMinutes ?? RULES.passengerCutoffMinutes
}

/** When booking closes for this sailing. */
export function closesAt(input: {
  scheduledDeparture: number
  bringingVehicle?: boolean
  passengerCutoffMinutes?: number | null
  vehicleCutoffMinutes?: number | null
}): number {
  return input.scheduledDeparture - cutoffMinutes(input) * MIN
}

/**
 * Can this still be booked?
 *
 * `status` closes it EARLIER than the clock would; it never re-opens one. A
 * sailing marked departed is gone whatever the timetable says, and a sailing the
 * timetable says has gone is gone whatever the status column says — the column
 * is only as current as the last person to touch it.
 */
export function isBookable(input: {
  scheduledDeparture: number
  status?: string
  seatsLeft?: number
  bringingVehicle?: boolean
  passengerCutoffMinutes?: number | null
  vehicleCutoffMinutes?: number | null
  /**
   * 'hazmat' when the operator's timetable footnotes this run as carrying
   * hazardous material. Culebra has two on a Saturday.
   */
  restricted?: string | null
}, now: number = Date.now()): boolean {
  /*
   * A restricted run is a real boat that really leaves, and it is not a
   * passenger service. It is refused HERE rather than filtered out of the list,
   * so that the sailing can still be shown — a gap where a 9 AM boat obviously
   * is would just look like the app is broken — while being impossible to sell.
   * Refusing it in the UI alone would leave the endpoint taking the money.
   */
  if (input.restricted) return false
  if (input.status && ['departed', 'arrived', 'cancelled', 'boarding'].includes(input.status)) return false
  if (input.seatsLeft != null && input.seatsLeft <= 0) return false
  return now < closesAt(input)
}

/**
 * Build the plan.
 *
 * Backwards from departure. Read the chain bottom-up and it is the order things
 * actually happen; read `leaveHomeAt` and it is the only number most people
 * want.
 */
export function buildPlan(input: PlanInput, now: number = Date.now()): FerryPlan {
  const steps: PlanStep[] = []
  const warnings: PlanWarning[] = []

  const depart = input.actualDeparture ?? input.scheduledDeparture
  const delayMin = input.actualDeparture
    ? Math.round((input.actualDeparture - input.scheduledDeparture) / MIN)
    : 0
  const arrive = input.scheduledArrival + delayMin * MIN

  const arriveBy: ArriveBy = input.arriveBy ?? 'drive'
  const vehicle = !!input.bringingVehicle

  const passengerCut = input.passengerCutoffMinutes ?? RULES.passengerCutoffMinutes
  const vehicleCut = input.vehicleCutoffMinutes ?? RULES.vehicleCutoffMinutes
  const cutoff = depart - (vehicle ? vehicleCut : passengerCut) * MIN

  const [from, to] = input.route.split('-')

  /* ── Forwards from here, because these are facts about the boat ─────────── */
  steps.push({
    id: 'depart', kind: 'depart',
    title: `${input.vesselName} departs`,
    detail: from ? `Leaves ${place(from)}` : undefined,
    at: depart,
  })
  steps.push({
    id: 'arrive-dest', kind: 'arriveDest',
    title: to ? `Arrive ${place(to)}` : 'Arrive',
    at: arrive,
  })
  steps.push({
    id: 'boarding', kind: 'boarding',
    title: 'Boarding opens',
    detail: 'Priority boarding is called first.',
    at: depart - RULES.boardingOpensMinutes * MIN,
  })

  /* ── Backwards from the cutoff, which is the fixed deadline ─────────────── */
  if (vehicle) {
    steps.push({
      id: 'vehicle-cutoff', kind: 'vehicleCutoff',
      title: 'Vehicle lane closes',
      detail: 'Your car has to be in the lane by now — not just at the terminal.',
      at: cutoff, isDeadline: true,
    })
    steps.push({
      id: 'check-in', kind: 'checkIn',
      title: 'Foot passenger check-in closes',
      at: depart - passengerCut * MIN,
    })
  } else {
    steps.push({
      id: 'check-in', kind: 'checkIn',
      title: 'Check-in closes',
      detail: 'The last moment your ticket is any use.',
      at: cutoff, isDeadline: true,
    })
  }

  // Be inside the terminal before the cutoff, not standing at it.
  const atTerminal = cutoff - RULES.terminalBufferMinutes * MIN
    - (input.hasTicket ? 0 : RULES.counterMinutes * MIN)

  if (!input.hasTicket) {
    steps.push({
      id: 'counter', kind: 'counter',
      title: 'Buy your ticket',
      detail: 'Skip this by booking in the app.',
      at: cutoff - RULES.terminalBufferMinutes * MIN - RULES.counterMinutes * MIN,
      durationMinutes: RULES.counterMinutes,
    })
  }

  steps.push({
    id: 'arrive-terminal', kind: 'arrive',
    title: 'Be at the terminal',
    at: atTerminal,
  })

  const park = RULES.parkingMinutes[arriveBy]
  const walk = RULES.toTerminalMinutes[arriveBy]
  if (park + walk > 0) {
    steps.push({
      id: 'park', kind: 'park',
      title: arriveBy === 'drive' ? 'Park and walk over' : 'Get dropped off',
      detail: arriveBy === 'drive' ? 'The lot fills on a Friday and the overflow is a walk.' : undefined,
      at: atTerminal - (park + walk) * MIN,
      durationMinutes: park + walk,
    })
  }

  /*
   * Leaving home is the whole point of the screen, and it is the one step that
   * cannot be computed without something only the traveller knows. With no
   * travel time the chain stops at the terminal and says so, rather than
   * inventing a number somebody would act on.
   */
  let leaveHomeAt: number | null = null
  if (input.travelMinutes != null && input.travelMinutes >= 0) {
    const startTravel = atTerminal - (park + walk) * MIN - input.travelMinutes * MIN
    steps.push({
      id: 'travel', kind: 'travel',
      title: 'On your way',
      detail: `About ${input.travelMinutes} minutes to the terminal.`,
      at: startTravel, durationMinutes: input.travelMinutes,
    })
    leaveHomeAt = startTravel
    steps.push({
      id: 'leave', kind: 'leave',
      title: 'Leave now',
      detail: 'Walk out the door.',
      at: startTravel,
    })
  } else {
    warnings.push({
      level: 'info',
      message: 'Tell us how far you are from the terminal and this plan will start at your front door.',
    })
  }

  steps.sort((a, b) => a.at - b.at || (a.kind === 'leave' ? -1 : 1))

  /* ── Warnings: only things somebody would act on ────────────────────────── */
  if (input.restricted === 'hazmat') {
    warnings.push({
      level: 'error',
      message: 'This is a hazardous-material sailing, not a passenger service. Book a different one.',
    })
  } else if (input.restricted === 'cargo') {
    warnings.push({ level: 'error', message: 'This sailing is cargo only, not a passenger service. Book a different one.' })
  } else if (input.restricted) {
    warnings.push({ level: 'error', message: 'This sailing is not confirmed as a passenger service. Book a different one.' })
  } else if (input.status === 'cancelled') {
    warnings.push({ level: 'error', message: 'This sailing is cancelled. Do not travel to the terminal.' })
  } else if (delayMin >= 15) {
    warnings.push({ level: 'warn', message: `Running ${delayMin} minutes late. Every time below has moved with it.` })
  }

  if (now > cutoff && input.status !== 'cancelled') {
    warnings.push({
      level: 'error',
      message: vehicle
        ? 'The vehicle lane for this sailing has closed.'
        : 'Check-in for this sailing has closed.',
    })
  } else if (leaveHomeAt != null && now > leaveHomeAt) {
    warnings.push({ level: 'warn', message: 'You should already be on your way.' })
  }

  if (vehicle) {
    warnings.push({
      level: 'info',
      message: `Bringing a car moves everything ${vehicleCut - passengerCut} minutes earlier.`,
    })
  }

  return {
    steps, warnings, leaveHomeAt,
    cutoffAt: cutoff,
    departAt: depart,
    arriveAt: arrive,
    doorToDoorMinutes: leaveHomeAt == null ? null : Math.round((arrive - leaveHomeAt) / MIN),
  }
}

/** The next thing to do, for the "what now" line at the top of the screen. */
export function nextStep(plan: FerryPlan, now: number = Date.now()): PlanStep | null {
  return plan.steps.find((s) => s.at > now) ?? null
}
